import React, { useState, useEffect } from 'react';
import {
    Avatar,
    Box,
    Checkbox,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ComputerIcon from '@mui/icons-material/Computer';
import AddTimeModal from '../AddTimeModal';
import SplitTime from '../../../../components/Projects/SplitTime';
import DeleteTime from '../../../../components/Projects/DeleteTime';

const projects = [
    {
        name: 'SkyGrid',
        desk: 'Worked Desk 1',
        initials: 'SG',
        color: '#6A1B9A',
        activity: 10,
        idle: 50,
        manual: 0,
        time: '10:36 am - 2:20 pm',
        duration: '3:44:13',
    },
    {
        name: 'Digital Marketing',
        desk: 'Worked Desk 3',
        initials: 'DM',
        color: '#1976D2',
        activity: 10,
        idle: 10,
        manual: 10,
        time: '2:21 pm - 3:23 pm',
        duration: '1:02:25',
    },
    {
        name: 'CodeFusion',
        desk: 'Worked Desk 2',
        initials: 'CF',
        color: '#2E7D32',
        activity: 10,
        idle: 8,
        manual: 0,
        time: '5:55 pm - 7:12 pm',
        duration: '1:16:11',
    },
    {
        name: 'TerraGear',
        desk: 'Worked Desk 2',
        initials: 'TG',
        color: '#F57C00',
        activity: 10,
        idle: 50,
        manual: 0,
        time: '10:26 am - 3:06 pm',
        duration: '4:39:35',
    },
    {
        name: 'HorizonGrove',
        desk: 'Worked Desk 6',
        initials: 'HG',
        color: '#5C6BC0',
        activity: 10,
        idle: 50,
        manual: 0,
        time: '9:54 am - 10:22 am',
        duration: '0:27:21',
    },
    {
        name: 'RiverGate',
        desk: 'Worked Desk 5',
        initials: 'RG',
        color: '#D32F2F',
        activity: 10,
        idle: 10,
        manual: 10,
        time: '10:25 am - 2:39 pm',
        duration: '4:14:39',
    },
];

const ProgressBar = ({ value, color }) => (
    <Box sx={{ width: 40, height: 6, borderRadius: 2, backgroundColor: '#e0e0e0', overflow: 'hidden' }}>
        <Box sx={{ width: `${value}%`, height: '100%', backgroundColor: color }} />
    </Box>
);



const CustomCheckbox = ({ checked, onChange }) => (
    <Checkbox
        checked={checked}
        onChange={onChange}
        icon={
            <Box
                sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    border: '1px solid #D0D5DD',
                    backgroundColor: '#E5E8EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M20 6L9 17L4 12"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Box>
        }
        checkedIcon={
            <Box
                sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    backgroundColor: '#1976d2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M20 6L9 17L4 12"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Box>
        }
    />
);

const DailyTimeSheets = () => {
    const [loading, setLoading] = useState(true);
    const [selectedValues, setSelectedValues] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuRowIndex, setMenuRowIndex] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalData, setModalData] = useState({
        project: null,
        type: '', // 'manual' or 'time'
        initialStart: '',
        initialEnd: '',
    });

    const [deleteOpen, setDeleteOpen] = useState(false)
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 1000);
        return () => clearTimeout(timer);
    }, []);

    const handleCheckboxChange = (name) => {
        setSelectedValues((prev) =>
            prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
        );
    };

    const handleMenuClick = (event, index) => {
        setAnchorEl(event.currentTarget);
        setMenuRowIndex(index);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setMenuRowIndex(null);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedValues(projects.map((p) => p.name));
        } else {
            setSelectedValues([]);
        }
    };


    // Function to open the modal for manual/time entry
    const handleCellClick = (project, type) => {
        // Convert the project's time to hours (simple example)
        // You might want to implement more sophisticated time parsing
        const [startTime, endTime] = project.time.split(' - ');
        const startHour = convertTimeToHour(startTime);
        const endHour = convertTimeToHour(endTime);

        setModalData({
            project,
            type,
            initialStart: startHour,
            initialEnd: endHour,
        });
        setModalOpen(true);
    };

    // Helper function to convert time string to hour number
    const convertTimeToHour = (timeStr) => {
        const [time, period] = timeStr.split(' ');
        const [hours, minutes] = time.split(':').map(Number);

        let hour = hours;
        if (period === 'pm' && hour !== 12) hour += 12;
        if (period === 'am' && hour === 12) hour = 0;

        return hour + (minutes / 60);
    };

    // Function to handle saving from the modal
    const handleSaveEntry = (newStart, newEnd, newType) => {
        // Here you would typically update your projects data
        console.log(`Saved entry for ${modalData.project.name}:`, {
            start: newStart,
            end: newEnd,
            type: newType,
        });
        setModalOpen(false);
    };

    return (
        <>
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table className='shadow-none' sx={{ boxShadow: 'none' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <CustomCheckbox
                                    checked={projects.length > 0 && selectedValues.length === projects.length}
                                    onChange={handleSelectAll}
                                />
                            </TableCell>
                            <TableCell>S.No.</TableCell>
                            <TableCell>Project</TableCell>
                            <TableCell align='center'>Source</TableCell>
                            <TableCell>Activity</TableCell>
                            <TableCell>Idle</TableCell>
                            <TableCell
                                onClick={() => handleCellClick(projects[0], 'manual')}
                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                            >
                                Manual
                            </TableCell>
                            <TableCell
                                align='center'
                                onClick={() => handleCellClick(projects[0], 'time')}
                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                            >
                                Time
                            </TableCell>
                            <TableCell align='center'>Duration</TableCell>
                            <TableCell align='center'>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading
                            ? [...Array(6)].map((_, i) => (
                                <TableRow key={i}>
                                    {[...Array(10)].map((_, j) => (
                                        <TableCell key={j}><Skeleton variant="text" /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                            : projects.map((project, index) => (
                                <TableRow key={index} hover>
                                    <TableCell padding="checkbox">
                                        <CustomCheckbox
                                            checked={selectedValues.includes(project.name)}
                                            onChange={() => handleCheckboxChange(project.name)}
                                        />
                                    </TableCell>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Avatar sx={{ bgcolor: project.color, width: 30, height: 30, fontSize: 14 }}>
                                                {project.initials}
                                            </Avatar>
                                            <Box>
                                                <Typography fontSize={14} fontWeight={500}>
                                                    {project.name}
                                                </Typography>
                                                <Typography fontSize={12} color="text.secondary">
                                                    {project.desk}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell align='center'><ComputerIcon fontSize="small" /></TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <ProgressBar value={project.activity} color="#1976D2" />
                                            <Typography fontSize={12}>{project.activity}%</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <ProgressBar value={project.idle} color="#EF5350" />
                                            <Typography fontSize={12}>{project.idle}%</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleCellClick(project, 'manual')}
                                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <ProgressBar value={project.manual} color="#FBC02D" />
                                            <Typography fontSize={12}>{project.manual}%</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        align='center'
                                        onClick={() => handleCellClick(project, 'time')}
                                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                    >
                                        <Typography fontSize={13}>{project.time}</Typography>
                                    </TableCell>
                                    <TableCell align='center'>
                                        <Typography fontSize={13} color="primary">{project.duration}</Typography>
                                    </TableCell>
                                    <TableCell align='center'>
                                        <IconButton onClick={(e) => handleMenuClick(e, index)}>
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={menuRowIndex === index}
                                            onClose={handleCloseMenu}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                            PaperProps={{
                                                sx: { '& .MuiMenuItem-root': { fontSize: '14px' } }
                                            }}
                                        >
                                            <MenuItem onClick={() => {
                                                handleCloseMenu();
                                                handleCellClick(project, 'edit');
                                            }}>
                                                Edit time entry
                                            </MenuItem>
                                            <MenuItem onClick={() => setOpen(true)}>
                                                Split time entry
                                            </MenuItem>
                                            <MenuItem

                                                // onClick={() => {
                                                //     handleCloseMenu();
                                                //     alert(`Delete project: ${project.name}`);
                                                // }}

                                                onClick={() => setDeleteOpen(true)}

                                            >
                                                Delete time entry
                                            </MenuItem>
                                        </Menu>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <AddTimeModal
                modalOpen={modalOpen}
                closeModal={() => setModalOpen(false)}
                handleAddEntry={handleSaveEntry}
                newStart={modalData.initialStart}
                setNewStart={(val) => setModalData(prev => ({ ...prev, initialStart: val }))}
                newEnd={modalData.initialEnd}
                setNewEnd={(val) => setModalData(prev => ({ ...prev, initialEnd: val }))}
                newType={modalData.type === 'manual' ? 'manual' : 'focus'}
                setNewType={(val) => setModalData(prev => ({ ...prev, type: val }))}
                data={[]} // You might want to pass relevant data here
                currentTime={24} // Adjust as needed
                ACTIVITIES={{
                    focus: { color: '#6A0DAD', label: 'Forgot to start/stop timer' },
                    break: { color: '#00BFFF', label: 'Used a wrong task/project' },
                    meeting: { color: '#FF8C00', label: 'Was AFK on a call' },
                    other: { color: '#008060', label: 'Other' },
                }}
                options={{ billAble: true }}
                toggleOption={() => { }} // Implement if needed
                selectedProject={modalData.project}
                setSelectedProject={(project) => setModalData(prev => ({ ...prev, project }))}
            />
            <SplitTime
                open={open}
                onClose={() => setOpen(false)}
            />

            <DeleteTime
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
            />
        </>
    );
};

export default DailyTimeSheets;
